import React, { useMemo, useState } from "react";
import { Clock, MapPin, Users, ArrowRight, RefreshCw } from "lucide-react";
import { usePlanning } from "../lib/usePlanning";
import { Lien } from "./Lien";
import { Segments } from "./ui";
import { CalendrierAnnee } from "../components/CalendrierAnnee";

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const COURT = { Lundi: "Lu", Mardi: "Ma", Mercredi: "Me", Jeudi: "Je", Vendredi: "Ve", Samedi: "Sa" };

const jourDuJour = () => JOURS[Math.max(0, new Date().getDay() - 1)] || "Lundi";

/* Le planning de l'école, au format téléphone : un jour à la fois, choisi par une
   pastille. Le tableau de la version bureau est illisible à 390 px — et surtout, depuis
   ici l'inscription est à deux taps : le cours, puis « M'inscrire ». */
export const TabPlanning = () => {
  const { courses, secours, secours_le, loading, error, reload } = usePlanning();
  const [jour, setJour] = useState(jourDuJour);
  // « L'année » : vacances, portes ouvertes, stages, spectacles d'un coup d'œil.
  const [vue, setVue] = useState("cours");

  const duJour = useMemo(() => {
    const cours = (courses || []).filter(
      (c) => c.day === jour && String(c.statut || "Actif") === "Actif");
    return cours.sort((a, b) => String(a.start).localeCompare(String(b.start)));
  }, [courses, jour]);

  const choix = <Segments testId="espace-planning-vue" valeur={vue} onChange={setVue}
    options={[{ id: "cours", label: "Les cours" }, { id: "annee", label: "L'année" }]} />;
  if (vue === "annee") return <div className="space-y-5">{choix}<CalendrierAnnee testId="espace-annee" /></div>;

  if (loading) {
    return (
      <div className="space-y-5">{choix}<div className="min-h-[40vh] grid place-items-center" data-testid="espace-planning-chargement">
        <RefreshCw size={20} className="animate-spin" style={{ color: "var(--primary)" }} />
      </div></div>
    );
  }

  if (error && !courses.length) {
    return (
      <div className="space-y-5">{choix}<div className="card-flat text-center" data-testid="espace-planning-erreur">
        <p className="text-sm text-[var(--muted)]">
          Le planning n'est pas joignable pour l'instant.
        </p>
        <button onClick={reload} className="btn-ghost mt-4 text-sm"
          data-testid="espace-planning-reessayer">
          Réessayer
        </button>
      </div></div>
    );
  }

  return (
    <div className="space-y-5" data-testid="espace-planning-view">
      {choix}
      <div className="flex gap-2 overflow-x-auto -mx-5 px-5 pb-1"
        style={{ scrollbarWidth: "none" }} data-testid="espace-planning-jours">
        {JOURS.map((j) => (
          <button key={j} onClick={() => setJour(j)}
            className="px-4 min-h-[44px] rounded-full text-sm font-medium whitespace-nowrap shrink-0 transition-colors"
            data-testid={`espace-planning-jour-${j.toLowerCase()}`}
            style={j === jour
              ? { background: "var(--primary)", color: "#fff" }
              : { background: "var(--primary-tint)", color: "var(--text)" }}>
            <span className="sm:hidden">{COURT[j]}</span>
            <span className="hidden sm:inline">{j}</span>
          </button>
        ))}
      </div>

      {duJour.length === 0 ? (
        <div className="card-flat">
          <p className="text-sm text-[var(--muted)]">Aucun cours le {jour.toLowerCase()}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {duJour.map((c) => {
            const complet = String(c.etat_places || "").toUpperCase() === "COMPLET";
            return (
              <div key={c.id} className="card-flat" data-testid={`espace-planning-cours-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-tight">{c.name}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--muted)] mt-2">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        <span className="font-mono">{c.start}–{c.end}</span>
                      </span>
                      {c.room && (
                        <span className="flex items-center gap-1"><MapPin size={12} />{c.room}</span>
                      )}
                      {c.teachers?.length > 0 && <span>{c.teachers.join(", ")}</span>}
                    </div>
                  </div>
                  <span className="text-[10px] px-2.5 py-1 rounded-full shrink-0 font-medium"
                    style={{ background: "var(--primary-tint)", color: "var(--primary-hover, #00A8AF)" }}>
                    {c.age_label}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-3 mt-4">
                  <span className="text-xs flex items-center gap-1"
                    style={{ color: complet ? "#B45309" : "#15803D" }}>
                    <Users size={12} />
                    {complet ? "Complet" : c.message_site || "Places disponibles"}
                  </span>
                  {!complet && (
                    <Lien href={`/inscription?type=semestriel&course=${encodeURIComponent(c.id)}`}
                      className="btn-primary text-sm"
                      data-testid={`espace-planning-inscrire-${c.id}`}>
                      M'inscrire <ArrowRight size={14} />
                    </Lien>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {secours && secours_le !== new Date().toISOString().slice(0, 10) && (
        <p className="text-[11px] text-[var(--muted)] text-center">
          Horaires enregistrés le {secours_le || "dernièrement"} — l'école n'est pas
          joignable, les inscriptions en ligne peuvent être indisponibles.
        </p>
      )}
    </div>
  );
};
